/* ============================================================
   BIOVIVE USA — main.js
   Nav, Mobile Menu, Accordion, Counters, GSAP Animations
   ============================================================ */

'use strict';

// ── Utility ──
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

// ── Init ──
document.addEventListener('DOMContentLoaded', () => {
  // ── Page fade-in veil ──
  const veil = document.getElementById('page-veil');
  if (veil) {
    // Double rAF ensures the browser paints opacity:1 first,
    // then the CSS transition to opacity:0 fires cleanly
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        veil.classList.add('is-gone');
        setTimeout(() => veil.remove(), 1200);
      });
    });
  }

  initNav();
  initMobileMenu();
  initSearch();
  initAnnouncement();
  initAccordion();
  initCounters();
  initSmoothScroll();
  initReadingProgress();
  initMagneticCTA();
  initHeroToggle();
  initSectorTabs();
  // Defer off-screen RAF loops until hero animation finishes
  setTimeout(() => {
    initTrustStrip();
    initScienceIconSpin();
  }, 1800);

  // GSAP path — registered after scripts load
  if (typeof gsap !== 'undefined') {
    if (typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);
    initGSAP();
  } else {
    // Graceful fallback: IntersectionObserver
    initIOFallback();
  }
});

/* ── NAV: transparent → solid on scroll + hide-on-down ── */
function initNav() {
  const nav = $('#nav');
  if (!nav) return;

  let ticking = false;

  const update = () => {
    const y = window.scrollY;
    nav.classList.toggle('nav--solid', y > 60);
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }, { passive: true });

  // Mega menu — hover intent
  $$('.nav__item--has-mega').forEach(item => {
    const btn = item.querySelector('.nav__link');
    const mega = item.querySelector('.nav__mega');
    if (!btn || !mega) return;

    let closeTimer;

    const open = () => {
      clearTimeout(closeTimer);
      btn.setAttribute('aria-expanded', 'true');
      mega.classList.add('nav__mega--open');
    };
    const close = () => {
      closeTimer = setTimeout(() => {
        btn.setAttribute('aria-expanded', 'false');
        mega.classList.remove('nav__mega--open');
      }, 160);
    };

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', close);
    btn.addEventListener('click', () => {
      const open_ = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open_));
      mega.classList.toggle('nav__mega--open', !open_);
    });
    btn.addEventListener('keydown', e => {
      if (e.key === 'Escape') { btn.setAttribute('aria-expanded', 'false'); mega.classList.remove('nav__mega--open'); btn.focus(); }
    });
  });
}

/* ── MOBILE MENU ── */
function initMobileMenu() {
  const hamburger = $('#navHamburger');
  const menu      = $('#mobileMenu');
  const closeBtn  = $('#mobileMenuClose');
  const nav       = $('#nav');
  if (!hamburger || !menu) return;

  const open = () => {
    menu.removeAttribute('hidden');
    hamburger.setAttribute('aria-expanded', 'true');
    nav?.classList.add('nav--menu-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => (menu.querySelector('button, a'))?.focus(), 80);
  };

  const close = () => {
    menu.setAttribute('hidden', '');
    hamburger.setAttribute('aria-expanded', 'false');
    nav?.classList.remove('nav--menu-open');
    document.body.style.overflow = '';
    hamburger.focus();
  };

  hamburger.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && !menu.hidden) close(); });

  // Mobile sub-accordion
  $$('.mobile-menu__parent', menu).forEach(btn => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const sub = btn.nextElementSibling;
      if (sub) sub.toggleAttribute('hidden', expanded);
    });
  });

  // Close menu on anchor nav
  $$('a', menu).forEach(a => {
    if (a.getAttribute('href')?.startsWith('#')) a.addEventListener('click', close);
  });
}

/* ── HEADER SEARCH (predictive) ── */
function initSearch() {
  const toggle   = $('#navSearchToggle');
  const panel    = $('#navSearch');
  const closeBtn = $('#navSearchClose');
  const input    = $('#navSearchInput');
  const results  = $('#navSearchResults');
  const nav      = $('#nav');
  if (!toggle || !panel || !input || !results) return;

  let debounceTimer;

  const open = () => {
    panel.removeAttribute('hidden');
    toggle.setAttribute('aria-expanded', 'true');
    nav?.classList.add('nav--search-open');
    setTimeout(() => input.focus(), 60);
  };

  const close = () => {
    panel.setAttribute('hidden', '');
    toggle.setAttribute('aria-expanded', 'false');
    nav?.classList.remove('nav--search-open');
  };

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) { close(); toggle.focus(); } else { open(); }
  });

  closeBtn?.addEventListener('click', () => { close(); toggle.focus(); });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      close();
      toggle.focus();
    }
  });

  document.addEventListener('click', e => {
    if (toggle.getAttribute('aria-expanded') !== 'true') return;
    if (!panel.contains(e.target) && !toggle.contains(e.target)) close();
  });

  // Predictive results via Section Rendering API
  input.addEventListener('input', () => {
    const query = input.value.trim();
    clearTimeout(debounceTimer);

    if (query.length < 2) {
      results.innerHTML = '';
      return;
    }

    debounceTimer = setTimeout(() => {
      const url = `/search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`;
      fetch(url)
        .then(res => (res.ok ? res.text() : Promise.reject()))
        .then(html => {
          if (query === input.value.trim()) results.innerHTML = html;
        })
        .catch(() => {});
    }, 250);
  });
}

/* ── ANNOUNCEMENT BAR CLOSE + SLOW MARQUEE ── */
function initAnnouncement() {
  const bar   = $('#announcementBar');
  const close = $('#closeAnnouncement');
  if (!bar || !close) return;

  // Slow JS-driven marquee — 20px per second
  const inner = bar.querySelector('.announcement-bar__inner');
  if (inner) {
    const PX_PER_SEC = 20;
    let offset = 0;
    let lastTime = null;
    let paused = false;

    inner.style.willChange = 'transform';
    inner.addEventListener('mouseenter', () => { paused = true; });
    inner.addEventListener('mouseleave', () => { paused = false; });

    function tick(ts) {
      if (lastTime !== null && !paused) {
        const delta = (ts - lastTime) / 1000;
        offset += PX_PER_SEC * delta;
        const half = inner.scrollWidth / 2;
        if (offset >= half) offset -= half;
        inner.style.transform = `translateX(-${offset}px)`;
      }
      lastTime = ts;
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  close.addEventListener('click', () => {
    bar.style.maxHeight  = bar.offsetHeight + 'px';
    bar.style.overflow   = 'hidden';
    requestAnimationFrame(() => {
      bar.style.transition = 'max-height 0.32s ease, opacity 0.28s ease';
      bar.style.maxHeight  = '0';
      bar.style.opacity    = '0';
    });
    setTimeout(() => {
      bar.remove();
    }, 340);
  });
}

/* ── SCIENCE ICON SLOW SPIN — matches announcement bar 20px/sec ── */
function initScienceIconSpin() {
  const icon = $('.science__visual-icon');
  if (!icon) return;

  const DEG_PER_SEC = (20 / (2 * Math.PI * 45)) * 360;
  let angle = 0;
  let lastTime = null;
  let rafId = null;
  let visible = false;

  function tick(ts) {
    if (lastTime !== null) {
      angle += DEG_PER_SEC * (ts - lastTime) / 1000;
      if (angle >= 360) angle -= 360;
      icon.style.transform = `rotate(${angle}deg)`;
    }
    lastTime = ts;
    if (visible) rafId = requestAnimationFrame(tick);
  }

  new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !visible) {
        visible = true;
        icon.style.willChange = 'transform';
        rafId = requestAnimationFrame(tick);
      } else if (!entry.isIntersecting && visible) {
        visible = false;
        icon.style.willChange = 'auto';
        lastTime = null;
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      }
    });
  }, { threshold: 0 }).observe(icon.closest('.science__visual') || icon);
}

/* ── TRUST STRIP SLOW MARQUEE ── */
function initTrustStrip() {
  const inner = document.querySelector('.trust-strip__inner');
  if (!inner) return;

  const PX_PER_SEC = 20;
  let offset = 0;
  let lastTime = null;
  let paused = false;
  let rafId = null;
  let visible = false;

  inner.addEventListener('mouseenter', () => { paused = true; });
  inner.addEventListener('mouseleave', () => { paused = false; });

  function tick(ts) {
    if (lastTime !== null && !paused) {
      const delta = (ts - lastTime) / 1000;
      offset += PX_PER_SEC * delta;
      const half = inner.scrollWidth / 2;
      if (offset >= half) offset -= half;
      inner.style.transform = `translateX(-${offset}px)`;
    }
    lastTime = ts;
    if (visible) rafId = requestAnimationFrame(tick);
  }

  new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !visible) {
        visible = true;
        inner.style.willChange = 'transform';
        rafId = requestAnimationFrame(tick);
      } else if (!entry.isIntersecting && visible) {
        visible = false;
        inner.style.willChange = 'auto';
        lastTime = null;
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      }
    });
  }, { threshold: 0 }).observe(inner.closest('.trust-strip') || inner);
}

/* ── SCIENCE ACCORDION ── */
function initAccordion() {
  const triggers = $$('.accordion__trigger');
  if (!triggers.length) return;

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const wasOpen   = trigger.getAttribute('aria-expanded') === 'true';
      const panelId   = trigger.getAttribute('aria-controls');
      const panel     = document.getElementById(panelId);
      const item      = trigger.closest('.accordion__item');

      // Close all
      triggers.forEach(t => {
        t.setAttribute('aria-expanded', 'false');
        t.closest('.accordion__item')?.classList.remove('accordion__item--open');
        const pid = t.getAttribute('aria-controls');
        const p   = pid && document.getElementById(pid);
        if (p) p.setAttribute('hidden', '');
      });

      // Open this one (unless it was already open)
      if (!wasOpen && panel) {
        trigger.setAttribute('aria-expanded', 'true');
        item?.classList.add('accordion__item--open');
        panel.removeAttribute('hidden');
      }
    });
  });

  // Open first by default
  const first = triggers[0];
  if (first) {
    const pid = first.getAttribute('aria-controls');
    const p   = pid && document.getElementById(pid);
    first.setAttribute('aria-expanded', 'true');
    first.closest('.accordion__item')?.classList.add('accordion__item--open');
    p?.removeAttribute('hidden');
  }
}

/* ── ANIMATED COUNTERS ── */
function initCounters() {
  const els = $$('[data-count]');
  if (!els.length) return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el     = entry.target;
      const target = parseInt(el.dataset.count, 10);
      const dur    = 1800;
      const start  = performance.now();

      const tick = now => {
        const t = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - t, 3);          // ease-out cubic
        el.textContent = Math.round(eased * target).toLocaleString();
        if (t < 1) requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
      observer.unobserve(el);
    });
  }, { threshold: 0.6 });

  els.forEach(el => observer.observe(el));
}

/* ── SMOOTH SCROLL (anchor links) ── */
function initSmoothScroll() {
  $$('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const id  = a.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const navH = $('#nav')?.offsetHeight || 80;
      const top  = target.getBoundingClientRect().top + window.scrollY - navH - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
}

/* ── GSAP + ScrollTrigger animations ── */
function initGSAP() {
  const ST = typeof ScrollTrigger !== 'undefined' ? ScrollTrigger : null;

  // ── Hero entrance — background fades in first, then text after delay ──
  const heroTl = gsap.timeline({
    delay: 0.3,
    onComplete() {
      $$('.hero__eyebrow, .hero__title-line, .hero__subtitle, .hero__ctas, .hero__stats')
        .forEach(el => { el.style.willChange = 'auto'; });
    }
  });

  // Background image fades in slowly from transparent to full opacity
  gsap.fromTo('.hero__bg',
    { opacity: 0 },
    { opacity: 1, duration: 3.2, ease: 'power1.inOut' }
  );

  heroTl
    .fromTo('.hero__eyebrow',
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 1.0, ease: 'power4.out', delay: 0.4 }
    )
    .fromTo('.hero__title-line',
      { opacity: 0, y: 70 },
      { opacity: 1, y: 0, duration: 1.4, ease: 'power4.out', stagger: 0.22 },
      '-=0.3'
    )
    .fromTo('[data-audience-content="home"].hero__subtitle--audience',
      { opacity: 0, y: 45 },
      { opacity: 1, y: 0, duration: 1.2, ease: 'power4.out' },
      '-=0.8'
    )
    .fromTo('[data-audience-content="home"].hero__ctas--audience',
      { opacity: 0, y: 25 },
      { opacity: 1, y: 0, duration: 1.0, ease: 'power3.out' },
      '-=0.8'
    )
    .fromTo('.hero__stats',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 1.0, ease: 'power3.out' },
      '-=0.7'
    );

  if (!ST) return;

  // ── Shared animation config — single source of truth for all scroll animations ──
  const EASE    = 'power2.out';  // softer, more premium than power3
  const DUR     = 1.0;           // midpoint: 0.85 was too fast, 1.2 was too slow
  const Y       = 28;            // subtle lift — not aggressive
  const TRIGGER = 'top 85%';    // consistent entry point

  // ── Generic fade-up ──
  $$('[data-anim="fade-up"]').forEach(el => {
    if (el.closest('.hero')) return;
    if (el.closest('[data-stagger]')) return;

    gsap.fromTo(el,
      { opacity: 0, y: Y },
      {
        opacity: 1, y: 0, duration: DUR, ease: EASE,
        scrollTrigger: { trigger: el, start: TRIGGER, once: true }
      }
    );
  });

  // ── Stagger containers ──
  $$('[data-stagger]').forEach(container => {
    const children = $$('[data-anim="fade-up"]', container);
    if (!children.length) return;

    gsap.fromTo(children,
      { opacity: 0, y: Y },
      {
        opacity: 1, y: 0, duration: DUR, ease: EASE, stagger: 0.09,
        scrollTrigger: { trigger: container, start: TRIGGER, once: true }
      }
    );
  });

  // ── Modality card stagger ──
  const cardGrid = $('.modalities__grid');
  if (cardGrid) {
    gsap.fromTo($$('[data-anim="card-reveal"]'),
      { opacity: 0, y: Y, scale: 0.97 },
      {
        opacity: 1, y: 0, scale: 1, duration: DUR, ease: EASE, stagger: 0.08,
        scrollTrigger: { trigger: cardGrid, start: TRIGGER, once: true }
      }
    );
  }

  // ── Featured product slide in ──
  gsap.fromTo('.featured-product__info',
    { opacity: 0, x: 36 },
    {
      opacity: 1, x: 0, duration: DUR, ease: EASE,
      scrollTrigger: { trigger: '.featured-product__inner', start: TRIGGER, once: true }
    }
  );


  // ── Modality card background parallax ──
  $$('.modality-card__bg').forEach(bg => {
    gsap.to(bg, {
      yPercent: -8,
      ease: 'none',
      scrollTrigger: {
        trigger: bg.closest('.modality-card'),
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1.5
      }
    });
  });

  // ── FC card background parallax ──
  $$('.fc-card__bg, .mod-card__bg').forEach(bg => {
    gsap.to(bg, {
      yPercent: -6,
      ease: 'none',
      scrollTrigger: {
        trigger: bg.closest('.fc-card, .mod-card'),
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1.5
      }
    });
  });

  // ── Collage scroll parallax ──
  initCollageParallax(ST);

  // ── Varied section entrances ──
  initVariedEntrances(ST, DUR, EASE, Y, TRIGGER);
}

/* ── IntersectionObserver fallback (no GSAP) ── */
function initIOFallback() {
  // Hero entrance — fade + slide via RAF stagger
  const heroEls = $$('.hero__eyebrow, .hero__title-line, .hero__subtitle, .hero__ctas, .hero__stats');
  heroEls.forEach((el, i) => {
    // Set starting transform immediately (no transition yet)
    el.style.transform = 'translateY(22px)';
    // Then in the next frame, attach the transition and trigger the end state
    const delay = 180 + i * 150;
    setTimeout(() => {
      requestAnimationFrame(() => {
        el.style.transition = 'opacity 0.85s cubic-bezier(0.16,1,0.3,1), transform 0.85s cubic-bezier(0.16,1,0.3,1)';
        requestAnimationFrame(() => {
          el.style.opacity   = '1';
          el.style.transform = 'translateY(0)';
        });
      });
    }, delay);
  });

  // Scroll-triggered fade-up for everything else
  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.remove('anim--hidden');
        entry.target.classList.add('anim--visible');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  $$('[data-anim], [data-stagger] > *').forEach(el => {
    if (!el.closest('.hero')) {
      el.classList.add('anim--hidden');
      io.observe(el);
    }
  });
}


/* ═══════════════════════════════════════════════════════════
   SUPPLIER ENGAGEMENT ENHANCEMENTS
   ═══════════════════════════════════════════════════════════ */

/* ── SECTOR SOLUTIONS: GRID CARD → DETAIL PANEL ── */
// CET-style: clicking "Find Out More" on a simple grid card reveals
// the matching rich detail panel and smooth-scrolls it into view.
function initSectorTabs() {
  const cards  = $$('.st-grid-card');
  const panels = $$('.st-panel');
  if (!cards.length || !panels.length) return;

  cards.forEach((card, index) => {
    card.addEventListener('click', () => {
      // Show matching panel, hide others
      panels.forEach((panel, i) => {
        if (i === index) {
          panel.classList.add('st-panel--active');
          panel.style.animation = 'stFadeIn 0.4s cubic-bezier(0.16,1,0.3,1) forwards';
        } else {
          panel.classList.remove('st-panel--active');
          panel.style.animation = '';
        }
      });

      // Highlight the selected card
      cards.forEach((c, i) => c.classList.toggle('st-grid-card--active', i === index));

      // Smooth scroll the revealed panel into view, accounting for fixed nav
      const targetPanel = panels[index];
      if (targetPanel) {
        const navH = document.getElementById('nav')?.offsetHeight || 88;
        const top  = targetPanel.getBoundingClientRect().top + window.scrollY - navH - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  });
}

/* ── HERO AUDIENCE TOGGLE ── */
// Uses CSS class `hero-aud-hidden` (display:none !important) instead of the
// HTML `hidden` attribute, which gets overridden by base CSS display:flex rules.
function initHeroToggle() {
  const toggleBtns = $$('.hero__toggle-btn');
  if (!toggleBtns.length) return;

  toggleBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const audience = btn.dataset.audience;

      // Update button active state
      toggleBtns.forEach(b => {
        b.classList.toggle('hero__toggle-btn--active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      // Show matching content, hide non-matching — class-based, no `hidden` attr
      $$('[data-audience-content]').forEach(el => {
        const matches = el.dataset.audienceContent === audience;
        if (matches) {
          el.classList.remove('hero-aud-hidden');
          // Brief fade-in via inline animation
          el.style.animation = 'audienceFadeIn 0.3s ease forwards';
        } else {
          el.classList.add('hero-aud-hidden');
          el.style.animation = '';
        }
      });
    });
  });
}

/* ── READING PROGRESS BAR ── */
function initReadingProgress() {
  const bar = document.getElementById('reading-progress');
  if (!bar) return;

  const update = () => {
    const scrollTop  = window.scrollY;
    const docHeight  = document.documentElement.scrollHeight - window.innerHeight;
    const pct        = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    bar.style.transform = `scaleX(${pct / 100})`;
  };

  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* ── MAGNETIC CTA BUTTON — disabled, buttons are static ── */
function initMagneticCTA() {}

/* ── HERO COLLAGE SCROLL PARALLAX (runs when GSAP + ST are available) ── */
function initCollageParallax(ST) {
  if (!ST) return;
  const items = $$('.hero__collage-item');
  if (!items.length) return;

  // Each item scrolls at a different rate — creates depth as the user scrolls
  const rates = [-0.12, 0.10, -0.08, 0.14, -0.06, 0.09];
  items.forEach((item, i) => {
    const rate = rates[i % rates.length];
    gsap.to(item, {
      yPercent: rate * 100,
      ease: 'none',
      scrollTrigger: {
        trigger: '#hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1.2
      }
    });
  });
}

/* ── VARIED SECTION ENTRANCE ANIMATIONS ── */
function initVariedEntrances(ST, DUR, EASE, Y, TRIGGER) {
  if (!ST) return;

  // Section headings reveal with a slight clip-path wipe (luxury editorial feel)
  $$('.section-header').forEach(header => {
    if (header.closest('.hero')) return;
    gsap.fromTo(header.querySelector('.section-eyebrow'),
      { opacity: 0, letterSpacing: '0.4em' },
      {
        opacity: 1, letterSpacing: '0.2em', duration: 1.0, ease: 'power2.out',
        scrollTrigger: { trigger: header, start: TRIGGER, once: true }
      }
    );
  });

  // Manifesto quote — scale from 0.95 instead of just fade
  const manifestoQuote = $('.manifesto__quote');
  if (manifestoQuote) {
    gsap.fromTo(manifestoQuote,
      { opacity: 0, scale: 0.96, y: 30 },
      {
        opacity: 1, scale: 1, y: 0, duration: 1.4, ease: 'power3.out',
        scrollTrigger: { trigger: manifestoQuote, start: 'top 85%', once: true }
      }
    );
  }
}
